import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Snackbar,
  Stack,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import React from "react";
import { usePropertyReportField } from "../hooks/usePropertyReportField";
import {
  PropertyReportFieldKey,
  propertyReportFieldDescriptions,
  FieldDescriptionDetail,
} from "@stratify/shared";
import { CustomMarkdownRenderer } from "../../../components/CustomMarkdownRenderer";
import { useFormattedCopy } from "../../agentChat/hooks/useFormattedCopy";

interface ReportFieldProps {
  userId: string;
  propertyId: string;
  title: string;
  fieldKey: PropertyReportFieldKey;
}

export const ReportField: React.FC<ReportFieldProps> = ({
  userId,
  propertyId,
  title,
  fieldKey,
}) => {
  const { fieldData, isLoading, error } = usePropertyReportField(
    userId,
    propertyId,
    fieldKey
  );

  // Generate unique IDs for copy functionality
  const valueElementId = `report-field-value-${propertyId}-${fieldKey}`;
  const justificationElementId = `report-field-justification-${propertyId}-${fieldKey}`;

  // Copy hooks for value and justification
  const {
    handleCopy: handleCopyValue,
    copySnackbarOpen: copyValueSnackbarOpen,
    handleSnackbarClose: handleValueSnackbarClose,
  } = useFormattedCopy({ elementId: valueElementId });

  const {
    handleCopy: handleCopyJustification,
    copySnackbarOpen: copyJustificationSnackbarOpen,
    handleSnackbarClose: handleJustificationSnackbarClose,
  } = useFormattedCopy({ elementId: justificationElementId });

  // Get the field description by traversing the nested structure using the dot notation
  const getFieldDescription = (
    key: PropertyReportFieldKey
  ): FieldDescriptionDetail | undefined => {
    const parts = key.split(".");
    let current: any = propertyReportFieldDescriptions;

    for (const part of parts) {
      if (!current?.[part]) return undefined;
      current = current[part];
    }

    return current as FieldDescriptionDetail;
  };

  const fieldDescription = getFieldDescription(fieldKey);
  const isAnalysisField = fieldDescription?.isAnalysisField || false;

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box sx={{ mb: 6 }}>
      <Typography
        variant="h5"
        color="primary"
        sx={{ fontWeight: 600 }}
        data-testid={`${fieldKey}-report-field-title`}
      >
        {title}
      </Typography>
      {fieldData ? (
        <>
          <Stack direction="row" alignItems="center" spacing={1} m={0} p={0}>
            <span
              id={valueElementId}
              data-testid={`${fieldKey}-report-field-value`}
            >
              <CustomMarkdownRenderer
                content={fieldData.value}
                showDocumentLink={true}
              />
            </span>
            <IconButton
              size="small"
              onClick={handleCopyValue}
              title="Copy value"
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Stack>
          {!isAnalysisField && fieldData.justification && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <span id={justificationElementId}>
                <Typography variant="body2" color="text.secondary">
                  <CustomMarkdownRenderer content={fieldData.justification} />
                </Typography>
              </span>
              <IconButton
                size="small"
                onClick={handleCopyJustification}
                title="Copy justification"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </>
      ) : (
        "No relevant information provided."
      )}

      {/* Snackbars for copy feedback */}
      <Snackbar
        open={copyValueSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleValueSnackbarClose}
        message="Copied to clipboard"
      />
      <Snackbar
        open={copyJustificationSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleJustificationSnackbarClose}
        message="Copied to clipboard"
      />
    </Box>
  );
};
